import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Tooltip from '../Tooltip';
import styles from './Tabs.module.scss';

/* ────────────────────────────────────────────────────────────
 * Tabs — pure controlled tab strip primitive.
 *
 *   <Tabs
 *     activeKey={key}
 *     onChange={setKey}
 *     variant="line"
 *     items={[
 *       { key: 'personal', label: 'Personal Details' },
 *       { key: 'contact',  label: 'Contact Details'  },
 *     ]}
 *   />
 *
 * Variants:
 *   - "line"      sliding-underline indicator (default — used by
 *                 every page-level tab nav including BaseMenu's
 *                 7 sub-tabs).
 *   - "pill"      Linear-style filled pill (good for sub-filters
 *                 nested inside a card).
 *   - "segmented" iOS-style joined buttons (good for binary or
 *                 ternary mode switches).
 *
 * The strip is pure — the consumer renders matching content next
 * to / below it based on `activeKey`.
 *
 * Narrow screens: the strip scrolls sideways with its scrollbar
 * hidden (see Tabs.module.scss), so on a phone a tab can sit fully
 * off-screen with nothing saying it exists. Two things fix that:
 *   - the active tab is scrolled into view on mount and whenever
 *     activeKey changes (so a deep-linked last tab is visible), and
 *   - an edge fade is drawn on whichever side has tabs scrolled out
 *     of view — measured, so a strip that fits shows no fade at all.
 * ──────────────────────────────────────────────────────────── */

const VARIANT_CLASS = {
  line: styles.variantLine,
  pill: styles.variantPill,
  segmented: styles.variantSegmented,
};

// Width of the edge fade, in px. Keep in sync with `--tabs-fade` in
// Tabs.module.scss. Scrolling the active tab into view leaves this much
// room past it, so the tab never ends up half-hidden under the fade.
const FADE_PX = 24;

// Sub-pixel widths can leave scrollWidth a pixel wider than clientWidth
// with nothing actually hidden; ignore overflow smaller than this.
const OVERFLOW_TOLERANCE = 1;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const Tabs = ({ activeKey, onChange, items, variant, className, ariaLabel, size }) => {
  const stripRef = useRef(null);
  const tabRefs = useRef({});
  const didMountRef = useRef(false);
  const [indicator, setIndicator] = useState({ left: 0, width: 0, visible: false });
  // Which edges currently hide tabs. Drives the fade classes.
  const [fade, setFade] = useState({ start: false, end: false });

  const measureIndicator = useCallback(() => {
    if (variant !== 'line') return;
    const node = tabRefs.current[activeKey];
    const strip = stripRef.current;
    if (!node || !strip) return;
    const stripRect = strip.getBoundingClientRect();
    const nodeRect = node.getBoundingClientRect();
    // `+ scrollLeft` puts the indicator in the strip's scrolled-content
    // coordinates: it is absolutely positioned inside the scrolling
    // strip, so it scrolls along with the tabs and needs no re-measure
    // when the strip is scrolled.
    const next = {
      left: nodeRect.left - stripRect.left + strip.scrollLeft,
      width: nodeRect.width,
      visible: true,
    };
    // Same values → keep the old object, so the resize observer below
    // does not cause a re-render every time it fires.
    setIndicator((prev) =>
      prev.left === next.left && prev.width === next.width && prev.visible
        ? prev
        : next
    );
  }, [activeKey, variant]);

  const measureOverflow = useCallback(() => {
    const strip = stripRef.current;
    if (!strip) return;
    const maxScroll = strip.scrollWidth - strip.clientWidth;
    const overflows = maxScroll > OVERFLOW_TOLERANCE;
    const start = overflows && strip.scrollLeft > OVERFLOW_TOLERANCE;
    const end = overflows && strip.scrollLeft < maxScroll - OVERFLOW_TOLERANCE;
    setFade((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
  }, []);

  // Scroll the STRIP (never the page — element.scrollIntoView would also
  // scroll the window vertically) just far enough to show the active tab
  // plus the fade's width beside it.
  const scrollActiveIntoView = useCallback(
    (behavior) => {
      const strip = stripRef.current;
      const node = tabRefs.current[activeKey];
      if (!strip || !node) return;
      const maxScroll = strip.scrollWidth - strip.clientWidth;
      if (maxScroll <= OVERFLOW_TOLERANCE) return;
      const stripRect = strip.getBoundingClientRect();
      const nodeRect = node.getBoundingClientRect();
      // Tab edges in scrolled-content coordinates. clientLeft removes the
      // strip's own left border (the pill variant has one).
      const start = nodeRect.left - stripRect.left - strip.clientLeft + strip.scrollLeft;
      const end = start + nodeRect.width;
      let target = null;
      if (start - FADE_PX < strip.scrollLeft) target = start - FADE_PX;
      else if (end + FADE_PX > strip.scrollLeft + strip.clientWidth) {
        target = end + FADE_PX - strip.clientWidth;
      }
      if (target === null) return;
      target = Math.max(0, Math.min(target, maxScroll));
      if (typeof strip.scrollTo === 'function') strip.scrollTo({ left: target, behavior });
      else strip.scrollLeft = target;
    },
    [activeKey]
  );

  // useLayoutEffect prevents a one-frame flash where the indicator
  // is in its old position after a tab change.
  useLayoutEffect(() => {
    measureIndicator();
    measureOverflow();
  }, [measureIndicator, measureOverflow, items]);

  // Bring the active tab into view — instantly on mount (nothing to
  // animate from), smoothly on later changes unless the user asked for
  // reduced motion. Keyed on activeKey only (via scrollActiveIntoView):
  // a count badge updating must not yank a strip the user scrolled.
  useLayoutEffect(() => {
    const animate = didMountRef.current && !prefersReducedMotion();
    didMountRef.current = true;
    scrollActiveIntoView(animate ? 'smooth' : 'auto');
    // An instant scroll is already final — refresh the fades now rather
    // than waiting for the scroll event. A smooth one keeps them in step
    // through onScroll as it animates.
    measureOverflow();
  }, [scrollActiveIntoView, measureOverflow]);

  // Re-measure when anything changes size: the strip (viewport resize,
  // sidebar toggle) or a tab (label / count change, a web font finishing
  // loading after first paint). Observed for every variant now — the
  // fade needs it even where there is no underline. ResizeObserver is
  // missing in jsdom and very old browsers; the window resize listener
  // still covers the common case there.
  const itemKeys = JSON.stringify(items.map((item) => item.key));
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const measureAll = () => {
      measureIndicator();
      measureOverflow();
    };
    window.addEventListener('resize', measureAll);
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measureAll);
      if (stripRef.current) ro.observe(stripRef.current);
      Object.values(tabRefs.current).forEach((node) => node && ro.observe(node));
    }
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measureAll);
    };
  }, [measureIndicator, measureOverflow, itemKeys]);

  const stripClass = [
    styles.tabs,
    VARIANT_CLASS[variant] || styles.variantPill,
    size === 'sm' ? styles.sizeSm : '',
    size === 'lg' ? styles.sizeLg : '',
    fade.start ? styles.fadeStart : '',
    fade.end ? styles.fadeEnd : '',
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={stripClass}
      ref={stripRef}
      onScroll={measureOverflow}
    >
      {items.map((item) => {
        const isActive = item.key === activeKey;
        const tabClass = [
          styles.tab,
          isActive ? styles.tabActive : '',
          item.disabled ? styles.tabDisabled : '',
        ]
          .filter(Boolean)
          .join(' ');

        const button = (
          <button
            key={item.key}
            ref={(el) => { tabRefs.current[item.key] = el; }}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-disabled={item.disabled || undefined}
            disabled={item.disabled}
            className={tabClass}
            onClick={() => !item.disabled && onChange(item.key,item)}
          >
            {item.icon && <span className={styles.tabIcon} aria-hidden="true">{item.icon}</span>}
            <span className={styles.tabLabel}>{item.label}</span>
            {typeof item.count === 'number' && (
              <span className={styles.tabCount}>{item.count}</span>
            )}
          </button>
        );

        // Optional explanation on hover / keyboard focus. The indicator is
        // measured from the button itself, so the wrapper does not move it.
        return item.tooltip ? (
          <Tooltip key={item.key} content={item.tooltip} placement="bottom">
            {button}
          </Tooltip>
        ) : (
          button
        );
      })}

      {variant === 'line' && (
        <span
          className={styles.indicator}
          aria-hidden="true"
          style={{
            transform: `translateX(${indicator.left}px)`,
            width: indicator.width,
            opacity: indicator.visible ? 1 : 0,
          }}
        />
      )}
    </div>
  );
};

Tabs.propTypes = {
  activeKey: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      icon: PropTypes.node,
      count: PropTypes.number,
      disabled: PropTypes.bool,
      tooltip: PropTypes.node,
    })
  ).isRequired,
  variant: PropTypes.oneOf(['line', 'pill', 'segmented']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
};

Tabs.defaultProps = {
  variant: 'line',
  size: 'md',
  className: '',
  ariaLabel: undefined,
};

export default Tabs;
